import React, { useEffect, useState } from "react";

import { instructorsService } from "api/services/instructors.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";

import type { InstructorListItem, InstructorProfile, Certificate } from "api/types/instructor.types";
import "pages/AdminPage.scss";

const PAGE_SIZE = 10;

const SellerManagement: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [instructors, setInstructors] = useState<InstructorListItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Detail modal
  const [selectedInstructor, setSelectedInstructor] = useState<InstructorProfile | null>(null);
  const [certs, setCerts] = useState<Certificate[]>([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchInstructors = async () => {
    setLoading(true);
    try {
      const res = await instructorsService.list();
      setInstructors(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstructors();
  }, []);

  // Client-side search + pagination
  const searchedList = search ? instructors.filter((i) => i.name.includes(search) || (i.brand_name ?? "").includes(search)) : instructors;

  const totalPages = Math.ceil(searchedList.length / PAGE_SIZE);
  const pagedList = searchedList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleViewDetail = async (userId: string) => {
    setShowDetailModal(true);
    setDetailLoading(true);
    try {
      const [profileRes, certsRes] = await Promise.all([instructorsService.get(userId), instructorsService.listCertificates({ user: userId })]);
      setSelectedInstructor(profileRes.data);
      setCerts(certsRes.data.results ?? []);
    } catch {
      // ignore
    } finally {
      setDetailLoading(false);
    }
  };

  const handleTogglePublic = async (userId: string, isPublic: boolean) => {
    await instructorsService.updateStatus(userId, !isPublic);
    fetchInstructors();
  };

  const columns: Column<InstructorListItem>[] = [
    {
      key: "profile_image_url",
      title: "프로필",
      width: "70px",
      render: (v) =>
        v ? (
          <img src={String(v)} alt="프로필" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
        ) : (
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "#E4E4E7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
            }}
          >
            👤
          </div>
        ),
    },
    { key: "name", title: "강사명", width: "120px" },
    { key: "brand_name", title: "소속 브랜드", width: "150px", render: (v) => String(v || "-") },
    {
      key: "introduction",
      title: "소개",
      render: (v) => {
        const text = String(v || "-");
        return <span style={{ color: "#71717A", fontSize: 13 }}>{text.length > 60 ? text.slice(0, 60) + "…" : text}</span>;
      },
    },
    {
      key: "sports",
      title: "종목",
      width: "120px",
      render: (v) => {
        const sports = v as Record<string, unknown>[];
        if (!sports?.length) return "-";
        return (
          sports
            .slice(0, 2)
            .map((s) => s.name ?? s)
            .join(", ") + (sports.length > 2 ? ` 외 ${sports.length - 2}` : "")
        );
      },
    },
  ];

  const CERT_STATUS_BADGE: Record<string, "active" | "pending" | "rejected"> = {
    approved: "active",
    pending: "pending",
    rejected: "rejected",
  };
  const CERT_STATUS_LABEL: Record<string, string> = {
    approved: "승인됨",
    pending: "대기",
    rejected: "거절됨",
  };

  return (
    <div className="admin-page">
      <ContentHeader title="강사 관리" description="버클에 등록된 강사 목록을 조회하고 관리합니다." />

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <Input placeholder="강사명 검색" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} style={{ width: 260 }} prefix="🔍" />
          <Button variant="secondary" size="sm" onClick={handleSearch}>
            검색
          </Button>
        </div>

        {loading ? <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>불러오는 중...</div> : <Table columns={columns} data={pagedList} rowKey="user_id" onRowClick={(row) => handleViewDetail(row.user_id)} />}
        <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
      </div>

      {/* Detail Modal */}
      <Modal
        open={showDetailModal}
        title="강사 상세 정보"
        onClose={() => setShowDetailModal(false)}
        footer={
          <>
            {selectedInstructor && (
              <Button
                variant={selectedInstructor.is_public ? "danger" : "primary"}
                onClick={() => {
                  handleTogglePublic(selectedInstructor.user_id, selectedInstructor.is_public ?? false);
                  setShowDetailModal(false);
                }}
              >
                {selectedInstructor.is_public ? "비공개 전환" : "공개 전환"}
              </Button>
            )}
            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
              닫기
            </Button>
          </>
        }
      >
        {detailLoading ? (
          <div style={{ padding: 32, textAlign: "center", color: "#71717A" }}>불러오는 중...</div>
        ) : selectedInstructor ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Profile header */}
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              {selectedInstructor.profile_image_url ? (
                <img src={selectedInstructor.profile_image_url} alt="프로필" style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover" }} />
              ) : (
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    background: "#E4E4E7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 28,
                  }}
                >
                  👤
                </div>
              )}
              <div>
                <div style={{ fontWeight: 600, fontSize: 18 }}>{selectedInstructor.name}</div>
                <div style={{ color: "#71717A", fontSize: 13 }}>{selectedInstructor.brand_name}</div>
                <Badge variant={selectedInstructor.is_public ? "active" : "draft"}>{selectedInstructor.is_public ? "공개" : "비공개"}</Badge>
              </div>
            </div>

            {/* Info rows */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
              <div>
                <div style={{ fontSize: 12, color: "#71717A", marginBottom: 2 }}>연락처</div>
                <div style={{ fontSize: 14 }}>{selectedInstructor.contact_phone || "-"}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#71717A", marginBottom: 2 }}>이메일</div>
                <div style={{ fontSize: 14 }}>{selectedInstructor.contact_email || "-"}</div>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: 12, color: "#71717A", marginBottom: 2 }}>소개</div>
                <div style={{ fontSize: 14, lineHeight: 1.6 }}>{selectedInstructor.introduction || "-"}</div>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: 12, color: "#71717A", marginBottom: 2 }}>관심사</div>
                <div style={{ fontSize: 14 }}>{selectedInstructor.interests || "-"}</div>
              </div>
            </div>

            {/* Certificates */}
            {certs.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>발급 자격증 ({certs.length})</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {certs.map((cert) => (
                    <div
                      key={cert.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        background: "#F4F4F5",
                        borderRadius: 8,
                      }}
                    >
                      <span style={{ fontSize: 13 }}>{cert.certificate_type}</span>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ fontSize: 12, color: "#71717A" }}>{cert.created_at.slice(0, 10)}</span>
                        <Badge variant={CERT_STATUS_BADGE[cert.status] ?? "draft"}>{CERT_STATUS_LABEL[cert.status] ?? cert.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default SellerManagement;
